import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { showSuccess, showError, showValidationMessage } from "@/lib/utils/notifications";
import { sendAnalytics } from "@/lib/utils/analytics";
import { useAppSelector } from "@/app/hooks";
import { selectUser } from "@/features/auth/authSlice";
import { isLibertyFinanceManagerRole } from "../utils/applicationPolicies";
import {
  addApplicationNote,
  deleteAdditionalApplicationFile,
  deleteAttachmentDefault,
  getApplicationNotes,
  getMailTrace,
  getSmsTrace,
  updateApplicantPreviousDefaultOverrides,
  uploadAdditionalApplicationFiles,
} from "../services/applicationService";
import { usePermission } from "@/hooks/usePermission";
import { applyTaggingToNotes } from "@/components/notes/MentionTextarea";
import { parseMailLogRow } from "../utils/mailLogHelpers";
import {
  validateAdditionalFileSelection,
  getNoteRowId,
  type AdditionalFileSelectionResult,
} from "../utils/notesTabHelpers";
import { LIBERTY_ROLES } from "@/constants/roles";
import {
  openFilePreview,
  openFilePreviewByPath,
} from "@/lib/utils/filePreview";
import type {
  NotesTabProps,
  NoteFilterType,
  NotesAttachment,
  NoteRow,
  NoteLogRow,
} from "../types/tabs";
import type { EditableNote } from "../types/utils";
import {
  ADDITIONAL_FILES_RECEIVED_TYPE,
  NOTE_ATTACHMENT_FILE_TYPE,
  NOTE_ATTACHMENT_MODULE,
  buildAdditionalImageFilesList,
  buildNoteImageFilesList,
  filterVisibleNotes,
  getDefaultApplicationNoteFilter,
  getErrorMessage,
  isAccessDeniedMessage,
  resolveNoteAttachmentIds,
} from "../components/tabs/notesTabLogic";

export function useNotesTab({
  applicationAllData,
  applicationData,
  applicantId,
  onRefresh,
}: NotesTabProps) {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const { hasPolicy } = usePermission();
  const currentUserRole = user?.current_role?.role_name || "";
  const canViewApplicationNotes = hasPolicy(["application_detail_add_notes_view"]);
  const canEditApplicationNotes = hasPolicy(["application_detail_add_notes_edit"]);
  const canExecutiveOverride = hasPolicy(["application_detail_executive_override"]);
  const canManageAdditionalFiles = hasPolicy(["application_edit"]);
  const isAdmin = currentUserRole === "ROLE_ADMIN";
  const isLibertyRole = (LIBERTY_ROLES as readonly string[]).includes(currentUserRole);
  const currentUserId = user?.user_id ?? null;
  const isLibertyFinanceManager = isLibertyFinanceManagerRole(user);

  const [editingNote, setEditingNote] = useState<EditableNote | null>(null);

  const applicationId = applicationAllData?.application_id;
  const embeddedNotes = applicationAllData?.notes;
  const [fetchedNotes, setFetchedNotes] = useState<unknown[] | null>(null);
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const notes: NoteRow[] = (
    fetchedNotes ?? (Array.isArray(embeddedNotes) ? embeddedNotes : [])
  ) as NoteRow[];
  const statusHistory = applicationAllData?.status_history || [];

  const [mailTraceRows, setMailTraceRows] = useState<NoteLogRow[] | undefined>(
    undefined,
  );
  const [smsTraceRows, setSmsTraceRows] = useState<NoteLogRow[] | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!applicantId) return;
    let cancelled = false;
    void (async () => {
      try {
        const [mailTrace, smsTrace] = await Promise.all([
          getMailTrace(applicantId),
          getSmsTrace(applicantId),
        ]);
        if (cancelled) return;
        setMailTraceRows(
          Array.isArray(mailTrace) ? (mailTrace as NoteLogRow[]) : [],
        );
        setSmsTraceRows(
          Array.isArray(smsTrace) ? (smsTrace as NoteLogRow[]) : [],
        );
      } catch {
        if (!cancelled) {
          setMailTraceRows(undefined);
          setSmsTraceRows(undefined);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applicantId]);

  useEffect(() => {
    if (applicationId == null || applicationId === "") return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await getApplicationNotes(applicationId);
        if (!cancelled) setFetchedNotes(rows);
      } catch {
        if (!cancelled) setFetchedNotes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applicationId, notesRefreshKey]);

  const mailLogsEmbedded =
    applicationAllData?.mail_logs || applicationData?.mail_logs || [];
  const smsLogsEmbedded =
    applicationAllData?.sms_logs || applicationData?.sms_logs || [];
  const mailLogs = mailTraceRows !== undefined ? mailTraceRows : mailLogsEmbedded;
  const smsLogs = smsTraceRows !== undefined ? smsTraceRows : smsLogsEmbedded;
  const mailLogsList = (
    Array.isArray(mailLogs) ? mailLogs : []
  ) as NoteLogRow[];
  const smsLogsList = (Array.isArray(smsLogs) ? smsLogs : []) as NoteLogRow[];

  const [noteFilter, setNoteFilter] = useState<NoteFilterType>(() =>
    getDefaultApplicationNoteFilter(currentUserRole),
  );

  useEffect(() => {
    if (!currentUserRole) return;
    setNoteFilter(getDefaultApplicationNoteFilter(currentUserRole));
  }, [currentUserRole]);
  const [newNote, setNewNote] = useState("");
  const [taggingContent, setTaggingContent] = useState<Record<string, string>>(
    {},
  );
  const [submittingNote, setSubmittingNote] = useState(false);
  const [fileLabel, setFileLabel] = useState("Choose File");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingLargeConfirmFilesRef = useRef<File[] | null>(null);
  const [mailConversationOpen, setMailConversationOpen] = useState(false);
  const [selectedMailId, setSelectedMailId] = useState<string | null>(null);
  const [executiveSubmitting, setExecutiveSubmitting] = useState(false);
  const [uploadingAdditionalFiles, setUploadingAdditionalFiles] = useState(false);
  const [removingNoteAttachmentId, setRemovingNoteAttachmentId] = useState<
    string | number | null
  >(null);

  const [largeFileConfirm, setLargeFileConfirm] = useState<{
    open: boolean;
    warnings: AdditionalFileSelectionResult["warnings"];
  }>({ open: false, warnings: [] });
  const [removeAdditionalFileConfirm, setRemoveAdditionalFileConfirm] =
    useState<{ open: boolean; attachmentId: string | number | null }>({
      open: false,
      attachmentId: null,
    });
  const [removeNoteAttachmentConfirm, setRemoveNoteAttachmentConfirm] =
    useState<{ open: boolean; fileId: string | number | null }>({
      open: false,
      fileId: null,
    });
  const [chargebackOverrideConfirm, setChargebackOverrideConfirm] = useState<{
    open: boolean;
    value: "1" | "0" | null;
  }>({ open: false, value: null });
  const [defaultOverrideConfirm, setDefaultOverrideConfirm] = useState<{
    open: boolean;
    value: "1" | "0" | null;
  }>({ open: false, value: null });

  const filteredNotes = filterVisibleNotes(notes, noteFilter, isAdmin);

  const accountSuspended = applicationData?.account_suspended === "1";
  const previousChargeback = applicationData?.previous_chargeback === "1";
  const previousDefault = applicationData?.previous_default === "1";

  const resetAdditionalFileInput = useCallback(() => {
    setSelectedFiles([]);
    setFileLabel("Choose File");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const redirectToApplicationList = useCallback(() => {
    navigate("/application", { replace: true });
  }, [navigate]);

  const performFileUpload = useCallback(
    async (filesOverride?: File[]) => {
      const toUpload = filesOverride ?? selectedFiles;
      if (!toUpload.length) return;
      setUploadingAdditionalFiles(true);
      try {
        await uploadAdditionalApplicationFiles(applicantId, toUpload);
        showSuccess("Additional file Added Successfully");
        resetAdditionalFileInput();
        setNotesRefreshKey((n) => n + 1);
        await onRefresh?.();
      } catch (error: unknown) {
        const message = getErrorMessage(error, "Failed to upload files");
        if (isLibertyFinanceManager && isAccessDeniedMessage(message)) {
          resetAdditionalFileInput();
          showValidationMessage("Access Denied", "Message", redirectToApplicationList);
          return;
        }
        showError(message);
      } finally {
        setUploadingAdditionalFiles(false);
      }
    },
    [
      applicantId,
      isLibertyFinanceManager,
      onRefresh,
      redirectToApplicationList,
      resetAdditionalFileInput,
      selectedFiles,
    ],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) {
        resetAdditionalFileInput();
        return;
      }
      if (uploadingAdditionalFiles) {
        showError("Please wait for the current upload to finish.");
        e.target.value = "";
        return;
      }

      const filesArray = Array.from(files);
      setSelectedFiles(filesArray);
      setFileLabel(
        filesArray.length === 1
          ? filesArray[0].name
          : `${filesArray.length} files selected`,
      );

      if (!canManageAdditionalFiles) return;

      const result = validateAdditionalFileSelection(filesArray);
      if (!result.ok) {
        result.errors.forEach((msg) => showError(msg));
        resetAdditionalFileInput();
        return;
      }
      if (result.warnings.length > 0) {
        pendingLargeConfirmFilesRef.current = filesArray;
        setLargeFileConfirm({ open: true, warnings: result.warnings });
        return;
      }
      void performFileUpload(filesArray);
    },
    [
      canManageAdditionalFiles,
      performFileUpload,
      resetAdditionalFileInput,
      uploadingAdditionalFiles,
    ],
  );

  const handleFileUpload = useCallback(async () => {
    if (!canManageAdditionalFiles) return;
    const result = validateAdditionalFileSelection(selectedFiles);
    if (!result.ok) {
      result.errors.forEach((msg) => showError(msg));
      return;
    }
    if (result.warnings.length > 0) {
      pendingLargeConfirmFilesRef.current = selectedFiles;
      setLargeFileConfirm({ open: true, warnings: result.warnings });
      return;
    }
    await performFileUpload();
  }, [canManageAdditionalFiles, performFileUpload, selectedFiles]);

  const handleRemoveAdditionalFile = useCallback(
    (attachmentId: string | number) => {
      if (!canManageAdditionalFiles) return;
      setRemoveAdditionalFileConfirm({ open: true, attachmentId });
    },
    [canManageAdditionalFiles],
  );

  const handlePreviewAdditionalFile = useCallback(
    (file: {
      attachment_id?: string | number;
      filepath?: string;
      filename?: string;
      name?: string;
    }) => {
      const fileName = file.filename || file.name || "Document";
      if (file.attachment_id != null && String(file.attachment_id) !== "") {
        void openFilePreview(
          file.attachment_id,
          ADDITIONAL_FILES_RECEIVED_TYPE,
          fileName,
          applicantId,
          NOTE_ATTACHMENT_MODULE,
          buildAdditionalImageFilesList(
            applicationData?.addition_files_received,
            applicantId,
          ),
        );
        return;
      }
      if (!file.filepath) return;
      openFilePreviewByPath(file.filepath, file.filename || file.name || "Document");
    },
    [applicantId, applicationData?.addition_files_received],
  );

  const performRemoveAdditionalFile = useCallback(async () => {
    const id = removeAdditionalFileConfirm.attachmentId;
    setRemoveAdditionalFileConfirm({ open: false, attachmentId: null });
    if (id === null || id === undefined) return;
    try {
      await deleteAdditionalApplicationFile(applicantId, id);
      showSuccess("Additional file Removed");
      setNotesRefreshKey((n) => n + 1);
      await onRefresh?.();
    } catch (error: unknown) {
      showError(
        error instanceof Error ? error.message : "Failed to remove file",
      );
    }
  }, [applicantId, onRefresh, removeAdditionalFileConfirm.attachmentId]);

  const handleRemoveNoteAttachment = useCallback(
    (attachment: {
      id?: string | number;
      attachment_id?: string | number;
      attachment_path?: string;
      attachment_name?: string;
    }) => {
      const fileId = attachment.id ?? attachment.attachment_id;
      if (fileId == null || fileId === "") {
        showError("Cannot remove this attachment (missing file id).");
        return;
      }
      if (removingNoteAttachmentId !== null) return;
      setRemoveNoteAttachmentConfirm({ open: true, fileId });
    },
    [removingNoteAttachmentId],
  );

  const handlePreviewNoteAttachment = useCallback(
    (attachment: NotesAttachment, allAttachments: NotesAttachment[] = []) => {
      const fileName = attachment.attachment_name || "Document";
      const ids = resolveNoteAttachmentIds(attachment);
      if (ids) {
        void openFilePreview(
          ids.fileId,
          NOTE_ATTACHMENT_FILE_TYPE,
          fileName,
          ids.refId,
          NOTE_ATTACHMENT_MODULE,
          buildNoteImageFilesList(allAttachments),
        );
        return;
      }
      if (!attachment.attachment_path) return;
      openFilePreviewByPath(attachment.attachment_path, fileName);
    },
    [],
  );

  const performRemoveNoteAttachment = useCallback(async () => {
    const id = removeNoteAttachmentConfirm.fileId;
    setRemoveNoteAttachmentConfirm({ open: false, fileId: null });
    if (id === null || id === undefined || id === "") return;
    setRemovingNoteAttachmentId(id);
    try {
      await deleteAttachmentDefault(id);
      showSuccess("Document file Removed");
      setNotesRefreshKey((n) => n + 1);
      await onRefresh?.();
    } catch (error: unknown) {
      showError(
        error instanceof Error ? error.message : "Failed to remove file",
      );
    } finally {
      setRemovingNoteAttachmentId(null);
    }
  }, [onRefresh, removeNoteAttachmentConfirm.fileId]);

  const handleChargebackExecutiveOverride = useCallback((value: "1" | "0") => {
    setChargebackOverrideConfirm({ open: true, value });
  }, []);

  const performChargebackExecutiveOverride = useCallback(async () => {
    const value = chargebackOverrideConfirm.value;
    setChargebackOverrideConfirm({ open: false, value: null });
    if (value !== "1" && value !== "0") return;
    setExecutiveSubmitting(true);
    try {
      await updateApplicantPreviousDefaultOverrides(applicantId, {
        previous_chargeback_executive_override: value,
      });
      sendAnalytics('click', 'previous_chargeback_executive_override required ', value);
      showSuccess("Previous Chargeback Executive Override Change Successfully");
      await onRefresh?.();
    } catch (error: unknown) {
      showError(
        error instanceof Error ? error.message : "Failed to update override",
      );
    } finally {
      setExecutiveSubmitting(false);
    }
  }, [applicantId, chargebackOverrideConfirm.value, onRefresh]);

  const handleDefaultExecutiveOverride = useCallback((value: "1" | "0") => {
    setDefaultOverrideConfirm({ open: true, value });
  }, []);

  const performDefaultExecutiveOverride = useCallback(async () => {
    const value = defaultOverrideConfirm.value;
    setDefaultOverrideConfirm({ open: false, value: null });
    if (value !== "1" && value !== "0") return;
    setExecutiveSubmitting(true);
    try {
      await updateApplicantPreviousDefaultOverrides(applicantId, {
        previous_default_executive_override: value,
        default_repaid: value === "1" ? "1" : "0",
      });
      sendAnalytics('click', 'previous_default_executive_override required ', value);
      showSuccess("Previous Default Executive Override Change Successfully");
      await onRefresh?.();
    } catch (error: unknown) {
      showError(
        error instanceof Error ? error.message : "Failed to update override",
      );
    } finally {
      setExecutiveSubmitting(false);
    }
  }, [applicantId, defaultOverrideConfirm.value, onRefresh]);

  const handleAddNote = useCallback(async () => {
    if (!canEditApplicationNotes) return;
    if (!newNote.trim()) {
      showError("Please enter a note");
      return;
    }
    if (applicationId == null || applicationId === "") {
      showError("Application ID not available.");
      return;
    }

    setSubmittingNote(true);
    try {
      const noteBody = applyTaggingToNotes(newNote.trim(), taggingContent);
      await addApplicationNote(applicationId, noteBody);
      sendAnalytics('click', 'add note', '');
      showSuccess("Note Added Successfully!");
      setNewNote("");
      setTaggingContent({});
      setNotesRefreshKey((n) => n + 1);
      await onRefresh?.();
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message;
      showError(typeof msg === "string" && msg ? msg : "Failed to add note");
    } finally {
      setSubmittingNote(false);
    }
  }, [
    applicationId,
    canEditApplicationNotes,
    newNote,
    onRefresh,
    taggingContent,
  ]);

  const handleViewConversationHistory = useCallback(
    (log?: unknown, index?: number) => {
      if (log !== undefined) {
        const parsed = parseMailLogRow(log, index ?? 0);
        setSelectedMailId(parsed.id);
      } else {
        setSelectedMailId(null);
      }
      setMailConversationOpen(true);
    },
    [],
  );

  const handleTaggingAdd = useCallback((displayKey: string, useridToken: string) => {
    setTaggingContent((prev) => ({
      ...prev,
      [displayKey]: useridToken,
    }));
  }, []);

  const handleNoteSaved = useCallback(() => {
    showSuccess("Note Edited Successfully!");
    setNotesRefreshKey((n) => n + 1);
  }, []);

  const handleLargeFileConfirmPrimary = useCallback(() => {
    setLargeFileConfirm({ open: false, warnings: [] });
    const pending = pendingLargeConfirmFilesRef.current;
    pendingLargeConfirmFilesRef.current = null;
    void performFileUpload(pending ?? undefined);
  }, [performFileUpload]);

  const handleLargeFileConfirmSecondary = useCallback(() => {
    setLargeFileConfirm({ open: false, warnings: [] });
    pendingLargeConfirmFilesRef.current = null;
    resetAdditionalFileInput();
  }, [resetAdditionalFileInput]);

  return {
    applicationAllData,
    applicationData,
    applicantId,
    canViewApplicationNotes,
    canEditApplicationNotes,
    canExecutiveOverride,
    canManageAdditionalFiles,
    isLibertyRole,
    currentUserId,
    accountSuspended,
    previousChargeback,
    previousDefault,
    executiveSubmitting,
    notes,
    filteredNotes,
    noteFilter,
    setNoteFilter,
    newNote,
    setNewNote,
    taggingContent,
    submittingNote,
    fileLabel,
    selectedFiles,
    fileInputRef,
    uploadingAdditionalFiles,
    removingNoteAttachmentId,
    statusHistory,
    mailLogs,
    mailLogsList,
    smsLogsList,
    mailConversationOpen,
    setMailConversationOpen,
    selectedMailId,
    editingNote,
    setEditingNote,
    largeFileConfirm,
    removeAdditionalFileConfirm,
    setRemoveAdditionalFileConfirm,
    removeNoteAttachmentConfirm,
    setRemoveNoteAttachmentConfirm,
    chargebackOverrideConfirm,
    setChargebackOverrideConfirm,
    defaultOverrideConfirm,
    setDefaultOverrideConfirm,
    handleFileSelect,
    handleFileUpload,
    handleRemoveAdditionalFile,
    handlePreviewAdditionalFile,
    performRemoveAdditionalFile,
    handleRemoveNoteAttachment,
    handlePreviewNoteAttachment,
    performRemoveNoteAttachment,
    handleChargebackExecutiveOverride,
    performChargebackExecutiveOverride,
    handleDefaultExecutiveOverride,
    performDefaultExecutiveOverride,
    handleAddNote,
    handleViewConversationHistory,
    handleTaggingAdd,
    handleNoteSaved,
    handleLargeFileConfirmPrimary,
    handleLargeFileConfirmSecondary,
    getNoteRowId,
  };
}

export type NotesTabState = ReturnType<typeof useNotesTab>;
